/**
 * File d'attente des notifications ephemeres (« toasts »).
 *
 * Volontairement minimal : pas de dependance externe (aucune lib de toast n'est installee), pas
 * de Provider React — un simple store observable consommé par `useSyncExternalStore`. Importable
 * depuis n'importe quelle page : `toast.success('...')`.
 */

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastItem {
    id: number;
    kind: ToastKind;
    message: string;
    /** Duree d'affichage en ms (decomptee hors pause au survol). */
    duration: number;
}

/** Une erreur doit rester plus longtemps qu'un succes : il faut le temps de la lire. */
export const TOAST_DURATIONS: Record<ToastKind, number> = { success: 5000, error: 8000, info: 5000 };

/** Nombre de notifications empilees : au-dela, les plus anciennes sont retirees. */
export const MAX_TOASTS = 3;

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = (): void => {
    listeners.forEach((listener) => listener());
};

export const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};

/** Reference stable tant que rien ne change : requis par `useSyncExternalStore`. */
export const getToasts = (): ToastItem[] => items;

export function pushToast(kind: ToastKind, message: string, duration?: number): number {
    if (message.trim() === '') {
        return -1;
    }

    const id = nextId++;

    items = [...items, { id, kind, message, duration: duration ?? TOAST_DURATIONS[kind] }].slice(-MAX_TOASTS);
    emit();

    return id;
}

export function dismissToast(id: number): void {
    const remaining = items.filter((item) => item.id !== id);

    if (remaining.length !== items.length) {
        items = remaining;
        emit();
    }
}

/** Vide la file (tests, deconnexion). */
export function clearToasts(): void {
    if (items.length > 0) {
        items = [];
        emit();
    }
}

export const toast = {
    success: (message: string, duration?: number) => pushToast('success', message, duration),
    error: (message: string, duration?: number) => pushToast('error', message, duration),
    info: (message: string, duration?: number) => pushToast('info', message, duration),
    dismiss: dismissToast,
    clear: clearToasts,
};
